// MessageFilterDrawer - MODULE
// ***********************************************************************************************************************
import * as React from "react";
import { IListFilterItem } from "../../../models/common/listFilterItem";
import { AppUtils } from "source/framework/appUtils";
import { Translations } from "ui/Scripts/source/models/translations";
import { Grid } from "@mui/material";
import { FilterSearchField } from "source/components/framework/Filters/FilterSearchField";
import { SaveDataObj } from "source/framework/saveData";
import { Base } from "ui/Scripts/source/framework/base";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { FilterButtons } from "source/components/framework/Filters/FilterButtons";
import { DrawerContent } from "source/components/framework/Drawer/DrawerContent";
import { PageFilterConditionDrawer } from "source/components/framework/Page/PageFilterConditionDrawer";
import { DatePicker } from "source/components/framework/DateTime";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { IMessagePageFilterLists, IMessagePageFilterState, IMessagePageFilterStateWithoutDates } from "../MessagePageFilter";

//MessageFilterDrawer
export interface IMessageFilterDrawerProp extends IMessagePageFilterLists, IMessagePageFilterState {
    open: boolean;
    filter: IListFilterItem;
    canRemoveSharedFilters: boolean;
    onSaveFilter: (saveAs: boolean, onGetGeneralFilter: () => any) => void;
    onRemoveFilter: (filterId: string) => void;
    onOk: (closeDialog: boolean, filterState: IMessagePageFilterState) => any;
    onCancel: () => void;
}

export interface IMessageFilterDrawerState extends IMessagePageFilterStateWithoutDates {
    startDateStr: string;
    endDateStr: string;
    startEndDateDisabled: boolean;
}

export class MessageFilterDrawer extends React.Component<IMessageFilterDrawerProp, IMessageFilterDrawerState> {
    private orgStateHash: string = "";

    getStateFromProps = (props: IMessageFilterDrawerProp): IMessageFilterDrawerState => {
        return {
            ...props,
            startDateStr: props.selectedStartDate ? Base.timeToDateStr(props.selectedStartDate) : "",
            endDateStr: props.selectedEndDate ? Base.timeToDateStr(props.selectedEndDate) : "",
            startEndDateDisabled: props.selectedDynamicTimeFilterId !== props.dynamicTimeFilters[0].id
        };
    };

    constructor(props: IMessageFilterDrawerProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getSaveDataFromState(props, this.state).hash;
    }

    componentDidUpdate(prevProps: IMessageFilterDrawerProp, prevState: IMessageFilterDrawerState): void {
        const props = this.props;
        const filterChanged = prevProps.filter?.getHash() !== props.filter?.getHash();
        const opened = !prevProps.open && props.open;
        if (!filterChanged && !opened) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(props, state).hash;
    }

    handleChangeDynamicTimeFilterId = (id: string) => {
        if (id.toInteger() > 0) {
            this.setState({ selectedDynamicTimeFilterId: id, startDateStr: "", endDateStr: "", startEndDateDisabled: true });
        } else {
            this.setState({ selectedDynamicTimeFilterId: id, startEndDateDisabled: false });
        }
    };

    handleStartDateChange = (startDateStr: string) => {
        this.setState({ startDateStr });
    };

    handleEndDateChange = (endDateStr: string) => {
        this.setState({ endDateStr });
    };

    handleChangeUnitIds = (selectedUnitIds: string[]) => {
        this.setState({
            selectedUnitIds
        });
    };

    handleChangeCompanyIds = (selectedCompanyIds: string[]) => {
        this.setState({
            selectedCompanyIds
        });
    };

    handleChangeModifyUserCodes = (selectedModifyUserCodes: string[]) => {
        this.setState({
            selectedModifyUserCodes
        });
    };

    getSaveDataFromState = (_props: IMessageFilterDrawerProp, state: IMessageFilterDrawerState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("startDate", state.startDateStr);
        result.append("endDate", state.endDateStr);
        result.append("selectedUnitIds", state.selectedUnitIds);
        result.append("selectedCompanyIds", state.selectedCompanyIds);
        result.append("selectedModifyUserCodes", state.selectedModifyUserCodes);
        result.append("selectedDynamicTimeFilterId", state.selectedDynamicTimeFilterId ? state.selectedDynamicTimeFilterId : null);
        return result;
    };

    static filterIsSet = (filterLists: IMessagePageFilterLists, filterState: IMessagePageFilterState): boolean => {
        return !!filterState.selectedStartDate || !!filterState.selectedEndDate ||
            !(Base.equalStrArrays(filterState.selectedUnitIds, [filterLists.units[0].id]) &&
            Base.equalStrArrays(filterState.selectedCompanyIds, [filterLists.companies[0].id]) &&
            Base.equalStrArrays(filterState.selectedModifyUserCodes, [filterLists.modifyUsers[0].id]) &&
            filterState.selectedDynamicTimeFilterId === filterLists.dynamicTimeFilters[0].id);
    };

    getSaveFilterState = (): IMessagePageFilterState => {
        const state = this.state;
        const selectedStartDate = state.startDateStr ? state.startDateStr.toUtcDate().getTime() : null;
        const selectedEndDate = state.endDateStr ? state.endDateStr.toUtcDate().getTime() : null;
        return { ...state, selectedStartDate, selectedEndDate };
    };

    handleSaveFilter = async (saveAs: boolean) => {
        const props = this.props;
        const filterState = this.getSaveFilterState();
        if (!saveAs) {
            props.onOk(false, filterState);
            await this.props.onSaveFilter(false, null);
        } else {
            await this.props.onSaveFilter(true, () => {
                return props.onOk(false, filterState);
            });
        }
    };

    static getClearedParametersState = (props: IMessagePageFilterLists) => {
        return {
            startDateStr: "",
            endDateStr: "",
            selectedUnitIds: [props.units[0].id],
            selectedCompanyIds: [props.companies[0].id],
            selectedModifyUserCodes: [props.modifyUsers[0].id],
            selectedDynamicTimeFilterId: props.dynamicTimeFilters[0].id
        };
    };

    handleClear = () => {
        const props = this.props;
        this.setState(
            MessageFilterDrawer.getClearedParametersState(props)
        );
    };

    handleSave = async () => {
        const props = this.props;
        props.onOk(true, this.getSaveFilterState());
    };

    handleCancel = async (checkChanges: boolean) => {
        AppUtils.cancelEditDialog(checkChanges, this.getSaveDataFromState(this.props, this.state).hash, this.orgStateHash, this.handleSave, this.props.onCancel, Translations.YouHaveUnapprovedChangesDoYouWantApproveChanges);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <PageFilterConditionDrawer
                open={props.open}
                onClose={() => this.handleCancel(false)}
            >
                <DrawerContent
                    content={
                        <>
                            {!!props.onSaveFilter && !!props.onRemoveFilter &&
                                <FilterSearchField
                                    filter={props.filter}
                                    canRemoveSharedFilters={props.canRemoveSharedFilters}
                                    onSaveFilter={props.onSaveFilter ? this.handleSaveFilter : null}
                                    onRemoveFilter={props.onRemoveFilter}
                                />
                            }
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Date}>
                                        <IdTitleDropdown
                                            selectedId={state.selectedDynamicTimeFilterId}
                                            items={props.dynamicTimeFilters}
                                            onChange={this.handleChangeDynamicTimeFilterId}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={null}>
                                        <DatePicker
                                            value={state.startDateStr}
                                            disabled={state.startEndDateDisabled}
                                            onChange={this.handleStartDateChange}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={null}>
                                        <DatePicker
                                            value={state.endDateStr}
                                            disabled={state.startEndDateDisabled}
                                            onChange={this.handleEndDateChange}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Company}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedCompanyIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            values={props.companies}
                                            onChange={this.handleChangeCompanyIds} />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Unit}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedUnitIds}
                                            values={props.units}
                                            onChange={this.handleChangeUnitIds} />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Publisher}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedModifyUserCodes}
                                            toggleAll={true}
                                            values={props.modifyUsers}
                                            onChange={this.handleChangeModifyUserCodes} />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        </>
                    }
                    actions={
                        <FilterButtons
                            onSave={this.handleSave}
                            onCancel={this.handleCancel}
                            onClear={this.handleClear}
                        />
                    }
                />
            </PageFilterConditionDrawer>
        );
    }
}
